import React, { useState } from 'react';

function SearchForm({ onSubmit }) {
  const [mode, setMode] = useState('manual');
  const [formData, setFormData] = useState({
    name: '',
    seeds: '',
    ai_generate_related: '',
    ai_generate_prefixes: '',
    ai_seed_count: '20',
    // Basic settings
    min_volume: '10000',
    ai_post_limit: '0',
    // Advanced CLI options
    hl: 'en',
    gl: 'US',
    max: '0',
    depth: '1',
    expand_mode: 'last',
    steps: 'harvest,ai_pre,volume,ai_post',
    sort: 'score',
    location_code: '2840',
    language_code: 'en',
    ai_model: 'gpt-5-nano',
    debug: true,
    save_ai_raw: true,
    max_volume: '',
    min_kd: '',
    max_kd: '',
  });

  const modes = [
    {
      id: 'manual',
      name: 'Manual Seeds',
      description: 'Use your own keyword seeds',
      icon: '⌨️'
    },
    {
      id: 'ai_related',
      name: 'AI Generate Seeds',
      description: 'AI generates related keywords from topic',
      icon: '🤖'
    },
    {
      id: 'ai_prefixes',
      name: 'AI Prefixes',
      description: 'AI generates prefix variations',
      icon: '🔄'
    },
    {
      id: 'hybrid',
      name: 'Manual + AI Seeds',
      description: 'Combine manual seeds with AI generation',
      icon: '⚡'
    },
    {
      id: 'custom',
      name: 'Custom Configuration',
      description: 'Full control over all CLI parameters',
      icon: '⚙️'
    }
  ];

  const countryLanguageOptions = [
    { value: 'US-en', label: 'United States (English)', hl: 'en', gl: 'US', location_code: '2840', language_code: 'en' },
    { value: 'UK-en', label: 'United Kingdom (English)', hl: 'en', gl: 'GB', location_code: '2826', language_code: 'en' },
    { value: 'NO-no', label: 'Norway (Norwegian)', hl: 'no', gl: 'NO', location_code: '2578', language_code: 'no' },
    { value: 'DE-de', label: 'Germany (German)', hl: 'de', gl: 'DE', location_code: '2276', language_code: 'de' },
    { value: 'FR-fr', label: 'France (French)', hl: 'fr', gl: 'FR', location_code: '2250', language_code: 'fr' },
    { value: 'CA-en', label: 'Canada (English)', hl: 'en', gl: 'CA', location_code: '2124', language_code: 'en' },
    { value: 'AU-en', label: 'Australia (English)', hl: 'en', gl: 'AU', location_code: '2036', language_code: 'en' },
    { value: 'SE-sv', label: 'Sweden (Swedish)', hl: 'sv', gl: 'SE', location_code: '2752', language_code: 'sv' },
    { value: 'DK-da', label: 'Denmark (Danish)', hl: 'da', gl: 'DK', location_code: '2208', language_code: 'da' },
    { value: 'NL-nl', label: 'Netherlands (Dutch)', hl: 'nl', gl: 'NL', location_code: '2528', language_code: 'nl' }
  ];

  const tooltips = {
    seeds: 'Simple seed words like "adhd", "autism", "food", "hiking" (comma-separated)',
    ai_generate_related: 'Subject topics like "mental health", "foods", "travel methods", or "workouts" that AI uses to generate seed words',
    ai_generate_prefixes: 'Base word(s) that AI will add prefixes to for creating longer, more specific search queries',
    hl: 'Host language - language of Google interface (en, de, fr, etc.)',
    gl: 'Geolocation - country code for Google search (US, GB, DE, etc.)',
    location_code: 'Numeric location code for search volume data. Find codes at: developers.google.com/google-ads/api/reference/data/geotargets',
    language_code: 'Language code for search volume data (en, de, fr, etc.)',
    max: 'Maximum keywords to harvest from Google suggestions (0 = unlimited)',
    depth: 'How many rounds of expansion: 1 = expand seeds only, 2 = expand seeds + their suggestions',
    expand_mode: 'Which keywords to expand in next round: "last" = only new suggestions (efficient), "all" = everything (comprehensive), "first" = only original seeds (focused)',
    steps: 'Processing pipeline: harvest (Google suggestions), ai_pre (AI filtering), volume (search data), ai_post (AI analysis)',
    sort: 'Sort results before limiting: determines which top keywords are kept when using max limits. Score = Volume × (100 - KD) - higher score means high volume + low difficulty',
    min_volume: 'Minimum monthly search volume filter',
    max_volume: 'Maximum monthly search volume filter',
    ai_post_limit: 'Max keywords to send to AI post-evaluation (saves costs). Set to 0 for unlimited.',
    min_kd: 'Minimum keyword difficulty score (0-100)',
    max_kd: 'Maximum keyword difficulty score (0-100)',
    ai_model: 'AI model to use for keyword analysis and evaluation',
    ai_seed_count: 'Number of AI-generated seeds to create when using AI generation modes',
    debug: 'Enable detailed logging and debug output (also auto-saves AI responses)',
  };

  const [showTooltip, setShowTooltip] = useState(null);
  const [selectedCountryLanguage, setSelectedCountryLanguage] = useState('US-en');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    let settings = {};
    
    if (mode === 'custom') {
      // For custom mode, use all form values directly
      settings = {
        mode,
        // Convert strings to appropriate types
        min_volume: formData.min_volume ? parseInt(formData.min_volume) : undefined,
        max_volume: formData.max_volume ? parseInt(formData.max_volume) : undefined,
        ai_post_limit: formData.ai_post_limit ? parseInt(formData.ai_post_limit) : undefined,
        max: formData.max ? parseInt(formData.max) : undefined,
        depth: formData.depth ? parseInt(formData.depth) : undefined,
        location_code: formData.location_code ? parseInt(formData.location_code) : undefined,
        min_kd: formData.min_kd ? parseInt(formData.min_kd) : undefined,
        max_kd: formData.max_kd ? parseInt(formData.max_kd) : undefined,
        // String values
        hl: formData.hl,
        gl: formData.gl,
        expand_mode: formData.expand_mode,
        steps: formData.steps,
        sort: formData.sort,
        language_code: formData.language_code,
        ai_model: formData.ai_model,
        // Boolean values
        debug: formData.debug,
        save_ai_raw: formData.save_ai_raw,
        filter_duplicates: true,
        // Seeds and AI generation
        seeds: formData.seeds,
        ai_generate_related: formData.ai_generate_related,
        ai_generate_prefixes: formData.ai_generate_prefixes,
        ai_seed_count: formData.ai_seed_count ? parseInt(formData.ai_seed_count) : 20
      };
    } else {
      // Predefined mode settings
      settings = {
        mode,
        min_volume: parseInt(formData.min_volume),
        ai_post_limit: parseInt(formData.ai_post_limit),
        ai_seed_count: parseInt(formData.ai_seed_count) || 20,
        hl: 'en',
        gl: 'US',
        max: 0,
        depth: mode === 'ai_related' ? 0 : 1,
        expand_mode: 'last',
        steps: 'harvest,ai_pre,volume,ai_post',
        sort: 'volume',
        location_code: 2840,
        language_code: 'en',
        debug: formData.debug,
        save_ai_raw: formData.debug,
        filter_duplicates: true,
        ai_model: 'gpt-5-nano'
      };

      // Add mode-specific data
      if (mode === 'ai_related') {
        settings.ai_generate_related = formData.ai_generate_related;
        settings.seeds = ''; // Empty for AI-only mode
      } else if (mode === 'ai_prefixes') {
        settings.ai_generate_prefixes = formData.seeds;
        settings.seeds = formData.seeds; // Use the same seeds
      } else if (mode === 'hybrid') {
        settings.seeds = formData.seeds;
        settings.ai_generate_related = formData.ai_generate_related;
      } else {
        settings.seeds = formData.seeds;
      }
    }

    // Determine the seeds value based on mode
    let seedsValue = '';
    if (mode === 'ai_related') {
      seedsValue = formData.ai_generate_related || 'ai-generated'; // Use topic or placeholder
    } else if (mode === 'ai_prefixes') {
      seedsValue = formData.seeds;
    } else {
      seedsValue = formData.seeds;
    }

    try {
      await onSubmit({
        name: formData.name,
        seeds: seedsValue,
        settings
      });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleCountryLanguageChange = (e) => {
    const selected = countryLanguageOptions.find(opt => opt.value === e.target.value);
    if (selected) {
      setSelectedCountryLanguage(e.target.value);
      setFormData(prev => ({
        ...prev,
        hl: selected.hl,
        gl: selected.gl,
        location_code: selected.location_code,
        language_code: selected.language_code
      }));
    }
  };

  const Tooltip = ({ field, children }) => (
    <div className="relative inline-flex items-baseline">
      {children}
      <button
        type="button"
        onMouseEnter={() => setShowTooltip(field)}
        onMouseLeave={() => setShowTooltip(null)}
        className="ml-1 w-3 h-3 bg-cyan-500/20 border border-cyan-500/30 rounded-full flex items-center justify-center text-cyan-400 text-xs hover:bg-cyan-500/30 transition-colors"
      >
        ?
      </button>
      {showTooltip === field && (
        <div className="absolute top-full left-0 mt-1 w-64 p-2 bg-slate-800 border border-slate-600 rounded-lg text-xs text-gray-300 z-50 shadow-xl">
          {tooltips[field]}
        </div>
      )}
    </div>
  );

  const getCurrentMode = () => modes.find(m => m.id === mode);

  return (
    <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-700/50 shadow-2xl p-4 mb-6">
      <div className="flex items-center space-x-2 mb-4">
        <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-white">Initialize New Scan</h2>
          <p className="text-xs text-gray-400">Deploy advanced keyword intelligence search</p>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Mode Selection */}
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-2">Scan Mode</label>
          <div className="grid grid-cols-2 gap-1.5">
            {modes.slice(0, 4).map((modeOption) => (
              <button
                key={modeOption.id}
                type="button"
                onClick={() => setMode(modeOption.id)}
                className={`p-2 rounded-lg border text-left transition-all ${
                  mode === modeOption.id
                    ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300'
                    : 'bg-slate-800/50 border-slate-600/50 text-gray-300 hover:border-slate-500/50'
                }`}
              >
                <div className="flex items-center space-x-1.5 mb-1">
                  <span className="text-sm">{modeOption.icon}</span>
                  <span className="text-xs font-medium">{modeOption.name}</span>
                </div>
                <p className="text-xs text-gray-400">{modeOption.description}</p>
              </button>
            ))}
          </div>
          {/* Custom mode - full width */}
          <button
            type="button"
            onClick={() => setMode('custom')}
            className={`w-full mt-1.5 p-2 rounded-lg border text-left transition-all ${
              mode === 'custom'
                ? 'bg-orange-500/20 border-orange-500/50 text-orange-300'
                : 'bg-slate-800/50 border-slate-600/50 text-gray-300 hover:border-slate-500/50'
            }`}
          >
            <div className="flex items-center space-x-1.5 mb-1">
              <span className="text-sm">⚙️</span>
              <span className="text-xs font-medium">Custom Configuration</span>
              <span className="text-xs text-gray-500">(Advanced)</span>
            </div>
            <p className="text-xs text-gray-400">Full control over all CLI parameters and options</p>
          </button>
        </div>

        {/* Search Name */}
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-2">Search Name</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-transparent transition-all text-sm"
            placeholder="e.g., ADHD Research Alpha"
            required
          />
        </div>

        {/* Dynamic Fields Based on Mode */}
        {(mode === 'manual' || mode === 'hybrid') && (
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-2">Manual Seeds</label>
            <textarea
              name="seeds"
              value={formData.seeds}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-transparent transition-all resize-none text-sm"
              placeholder="adhd, quiz, workout, recipe"
              required={mode === 'manual'}
            />
          </div>
        )}

        {(mode === 'ai_related' || mode === 'hybrid') && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2">
                {mode === 'ai_related' ? 'AI Seed Topic' : 'AI Seed Topic (additional)'}
              </label>
              <input
                type="text"
                name="ai_generate_related"
                value={formData.ai_generate_related}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-transparent transition-all text-sm"
                placeholder="workout"
                required={mode === 'ai_related'}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-2">
                AI Seed Count
              </label>
              <input
                type="number"
                name="ai_seed_count"
                value={formData.ai_seed_count}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-transparent transition-all text-sm"
                placeholder="20"
                min="1"
                max="50"
              />
            </div>
          </div>
        )}

        {mode === 'ai_prefixes' && (
          <div>
            <label className="block text-xs font-medium text-gray-300 mb-2">Base Keyword for Prefixes</label>
            <input
              type="text"
              name="seeds"
              value={formData.seeds}
              onChange={handleChange}
              className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-transparent transition-all text-sm"
              placeholder="quiz"
              required
            />
          </div>
        )}

        {/* Country/Language Selection for all modes */}
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Country/Language</label>
          <select
            value={selectedCountryLanguage}
            onChange={handleCountryLanguageChange}
            className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500"
          >
            {countryLanguageOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Advanced Settings */}
        {mode !== 'custom' ? (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Min Volume</label>
                <input
                  type="number"
                  name="min_volume"
                  value={formData.min_volume}
                  onChange={handleChange}
                  className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  placeholder="10000"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">AI Post Limit</label>
                <input
                  type="number"
                  name="ai_post_limit"
                  value={formData.ai_post_limit}
                  onChange={handleChange}
                  className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  placeholder="150"
                />
              </div>
            </div>
            
            {/* Debug Mode Toggle */}
            <div className="border border-slate-600/50 rounded-lg p-2 bg-slate-800/20">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="debug"
                  checked={formData.debug}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    debug: e.target.checked,
                    save_ai_raw: e.target.checked || prev.save_ai_raw
                  }))}
                  className="rounded border-slate-600 bg-slate-700 text-gray-400"
                />
                <div>
                  <span className="text-xs text-gray-300">Debug Mode</span>
                  <p className="text-xs text-gray-400">Enable detailed logging of all processing steps</p>
                </div>
              </label>
            </div>
          </div>
        ) : (
          /* Custom Configuration - All CLI Options */
          <div className="space-y-4 border border-orange-500/30 rounded-lg p-3 bg-orange-500/5">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-orange-400 text-sm">⚙️</span>
              <span className="text-xs font-medium text-orange-300">Advanced CLI Configuration</span>
            </div>
            
            {/* Input Sources */}
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-gray-300 border-b border-gray-600 pb-1">Input Sources</h4>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Tooltip field="seeds">
                    <label className="flex items-center text-xs text-gray-300 mb-1">Manual Seeds</label>
                  </Tooltip>
                  <textarea
                    name="seeds"
                    value={formData.seeds}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs resize-none"
                    placeholder="adhd,autism"
                  />
                </div>
                <div>
                  <Tooltip field="ai_generate_related">
                    <label className="flex items-center text-xs text-gray-300 mb-1">AI Seed Topic</label>
                  </Tooltip>
                  <input
                    type="text"
                    name="ai_generate_related"
                    value={formData.ai_generate_related}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                    placeholder="foods"
                  />
                </div>
                <div>
                  <Tooltip field="ai_seed_count">
                    <label className="flex items-center text-xs text-gray-300 mb-1">AI Seed Count</label>
                  </Tooltip>
                  <input
                    type="number"
                    name="ai_seed_count"
                    value={formData.ai_seed_count}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                    placeholder="20"
                    min="1"
                    max="50"
                  />
                </div>
              </div>
              <div>
                <Tooltip field="ai_generate_prefixes">
                  <label className="flex items-center text-xs text-gray-300 mb-1">AI Prefix Generator</label>
                </Tooltip>
                <input
                  type="text"
                  name="ai_generate_prefixes"
                  value={formData.ai_generate_prefixes}
                  onChange={handleChange}
                  className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                  placeholder="adhd"
                />
              </div>
            </div>

            {/* Location & Language */}
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-gray-300 border-b border-gray-600 pb-1">Location & Language</h4>
              
              
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <Tooltip field="hl">
                    <label className="flex items-center text-xs text-gray-300 mb-1">HL</label>
                  </Tooltip>
                  <input
                    type="text"
                    name="hl"
                    value={formData.hl}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                    placeholder="en"
                  />
                </div>
                <div>
                  <Tooltip field="gl">
                    <label className="flex items-center text-xs text-gray-300 mb-1">GL</label>
                  </Tooltip>
                  <input
                    type="text"
                    name="gl"
                    value={formData.gl}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                    placeholder="US"
                  />
                </div>
                <div>
                  <Tooltip field="location_code">
                    <label className="flex items-center text-xs text-gray-300 mb-1">Location Code</label>
                  </Tooltip>
                  <input
                    type="number"
                    name="location_code"
                    value={formData.location_code}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                    placeholder="2840"
                  />
                </div>
                <div>
                  <Tooltip field="language_code">
                    <label className="flex items-center text-xs text-gray-300 mb-1">Language Code</label>
                  </Tooltip>
                  <input
                    type="text"
                    name="language_code"
                    value={formData.language_code}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                    placeholder="en"
                  />
                </div>
              </div>
            </div>

            {/* Processing Options */}
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-gray-300 border-b border-gray-600 pb-1">Processing Options</h4>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Tooltip field="max">
                    <label className="flex items-center text-xs text-gray-300 mb-1">Max Keywords</label>
                  </Tooltip>
                  <input
                    type="number"
                    name="max"
                    value={formData.max}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                    placeholder="0"
                  />
                </div>
                <div>
                  <Tooltip field="depth">
                    <label className="flex items-center text-xs text-gray-300 mb-1">Depth</label>
                  </Tooltip>
                  <input
                    type="number"
                    name="depth"
                    value={formData.depth}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                    placeholder="1"
                  />
                </div>
                <div>
                  <Tooltip field="expand_mode">
                    <label className="flex items-center text-xs text-gray-300 mb-1">Expand Mode</label>
                  </Tooltip>
                  <select
                    name="expand_mode"
                    value={formData.expand_mode}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                  >
                    <option value="last">Last</option>
                    <option value="all">All</option>
                    <option value="first">First</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Tooltip field="steps">
                    <label className="flex items-center text-xs text-gray-300 mb-1">Steps</label>
                  </Tooltip>
                  <input
                    type="text"
                    name="steps"
                    value={formData.steps}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                    placeholder="harvest,ai_pre,volume,ai_post"
                  />
                </div>
                <div>
                  <Tooltip field="sort">
                    <label className="flex items-center text-xs text-gray-300 mb-1">Sort</label>
                  </Tooltip>
                  <select
                    name="sort"
                    value={formData.sort}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                  >
                    <option value="score">Score</option>
                    <option value="volume">Volume</option>
                    <option value="kd">KD</option>
                    <option value="cpc">CPC</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Filtering Options */}
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-gray-300 border-b border-gray-600 pb-1">Filtering Options</h4>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Tooltip field="min_volume">
                    <label className="flex items-center text-xs text-gray-300 mb-1">Min Volume</label>
                  </Tooltip>
                  <input
                    type="number"
                    name="min_volume"
                    value={formData.min_volume}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                    placeholder="10000"
                  />
                </div>
                <div>
                  <Tooltip field="max_volume">
                    <label className="flex items-center text-xs text-gray-300 mb-1">Max Volume</label>
                  </Tooltip>
                  <input
                    type="number"
                    name="max_volume"
                    value={formData.max_volume}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                    placeholder=""
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Tooltip field="ai_post_limit">
                    <label className="flex items-center text-xs text-gray-300 mb-1">AI Post Limit</label>
                  </Tooltip>
                  <input
                    type="number"
                    name="ai_post_limit"
                    value={formData.ai_post_limit}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                    placeholder="150"
                  />
                </div>
                <div>
                  <Tooltip field="min_kd">
                    <label className="flex items-center text-xs text-gray-300 mb-1">Min KD</label>
                  </Tooltip>
                  <input
                    type="number"
                    name="min_kd"
                    value={formData.min_kd}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                    placeholder=""
                  />
                </div>
                <div>
                  <Tooltip field="max_kd">
                    <label className="flex items-center text-xs text-gray-300 mb-1">Max KD</label>
                  </Tooltip>
                  <input
                    type="number"
                    name="max_kd"
                    value={formData.max_kd}
                    onChange={handleChange}
                    className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                    placeholder=""
                  />
                </div>
              </div>
            </div>

            {/* AI Configuration */}
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-gray-300 border-b border-gray-600 pb-1">AI Configuration</h4>
              <div>
                <Tooltip field="ai_model">
                  <label className="flex items-center text-xs text-gray-300 mb-1">AI Model</label>
                </Tooltip>
                <select
                  name="ai_model"
                  value={formData.ai_model}
                  onChange={handleChange}
                  className="w-full px-2 py-1 bg-slate-900/50 border border-slate-600/50 rounded text-white text-xs"
                >
                  <option value="gpt-5-nano">GPT-5 Nano</option>
                  <option value="gpt-5-mini">GPT-5 Mini</option>
                </select>
              </div>
            </div>

            {/* Boolean Options */}
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-gray-300 border-b border-gray-600 pb-1">Options</h4>
              <div className="grid grid-cols-2 gap-2">
                <Tooltip field="debug">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      name="debug"
                      checked={formData.debug}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        debug: e.target.checked,
                        save_ai_raw: e.target.checked || prev.save_ai_raw
                      }))}
                      className="rounded border-slate-600 bg-slate-700 text-cyan-500"
                    />
                    <span className="text-xs text-gray-300">Debug Mode</span>
                  </label>
                </Tooltip>
              </div>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-2 px-4 rounded-lg font-medium transition-all duration-200 shadow-lg border text-sm ${
            isSubmitting 
              ? 'bg-slate-600 text-slate-400 border-slate-500 cursor-not-allowed' 
              : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-purple-500/25 hover:shadow-purple-500/40 border-purple-400/20'
          }`}
        >
          <span className="flex items-center justify-center space-x-2">
            {isSubmitting ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Initializing...</span>
              </>
            ) : (
              <>
                <span>{getCurrentMode().icon}</span>
                <span>Initialize {getCurrentMode().name}</span>
              </>
            )}
          </span>
        </button>
      </form>
    </div>
  );
}

export default SearchForm;